import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './LedgerPayments.css';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, opts = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } });
};

// Manual payment methods. Credit cards are handled separately by the Stripe
// payment link, so they're intentionally not in this list. The list is
// filtered per parent type — customer-facing buckets (Retainer/Credit) are
// suppressed on POs (outbound vendor payments) and SRISD is suppressed on
// invoices/proposals per the tenant's usage pattern.
const ALL_PAYMENT_METHODS = ['Personal Check', 'Venmo', 'Zelle', 'ACH Bank Transfer', 'Cash', 'Retainer', 'Credit', 'SRISD'];
const methodsFor = (parentType) => {
  if (parentType === 'purchase_order') {
    return ALL_PAYMENT_METHODS.filter(m => m !== 'Retainer' && m !== 'Credit');
  }
  // invoice + quote (proposal)
  return ALL_PAYMENT_METHODS.filter(m => m !== 'SRISD');
};
const fmt = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Local calendar date (YYYY-MM-DD) — avoids the UTC off-by-one from toISOString().
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Parse date-only values as LOCAL (not UTC) so "2026-08-21" doesn't show as 8/20.
const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
  return new Date(s.replace(' ', 'T')).toLocaleDateString();
};
const currentUserId = () => {
  try { return (JSON.parse(sessionStorage.getItem('user') || 'null') || {}).id || null; }
  catch (e) { return null; }
};

// Partial/full payment tracker for an invoice or proposal. Shows the total,
// amount paid and balance due, lists recorded payments, and lets you record a
// new one with a method + notes. Balance is computed against `total` (the live
// ledger grand total passed in).
const LedgerPayments = ({ parentType, parentId, total }) => {
  const methods = methodsFor(parentType);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: '', method: methods[0], notes: '', payment_date: today() });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!parentId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/ledger-payments.php?parent_type=${encodeURIComponent(parentType)}&parent_id=${encodeURIComponent(parentId)}`);
      const data = await res.json();
      if (data.success) setPayments(data.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [parentType, parentId]);

  useEffect(() => { load(); }, [load]);

  const totalPaid = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balance = (Number(total) || 0) - totalPaid;
  const paidInFull = (Number(total) || 0) > 0 && balance <= 0.005;

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const payFull = () => set({ amount: balance > 0 ? balance.toFixed(2) : '0.00' });

  const add = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError('Enter a payment amount.'); return; }
    setSaving(true); setError('');
    try {
      const res = await authFetch(`${CHAT_API_BASE}/ledger-payments.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_type: parentType,
          parent_id: parentId,
          amount: Math.round(amt * 100) / 100,
          method: form.method,
          notes: form.notes.trim(),
          payment_date: form.payment_date,
          user_id: currentUserId(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.data) {
        setPayments(prev => [...prev, data.data]);
        setForm({ amount: '', method: form.method, notes: '', payment_date: today() });
      } else {
        setError(data.message || 'Failed to record payment');
      }
    } catch (e) {
      setError('Failed to record payment: ' + e.message);
    }
    setSaving(false);
  };

  const remove = async (p) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/ledger-payments.php?id=${encodeURIComponent(p.id)}&user_id=${encodeURIComponent(currentUserId() || '')}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) setPayments(prev => prev.filter(x => x.id !== p.id));
      else alert(data.message || 'Failed to delete payment');
    } catch (e) {
      alert('Failed to delete payment: ' + e.message);
    }
  };

  return (
    <div className="ledger-payments">
      <div className={`lp-summary ${paidInFull ? 'paid-full' : ''}`}>
        <div className="lp-row"><span className="lp-lbl">Total</span><span className="lp-amt">{fmt(total)}</span></div>
        <div className="lp-row"><span className="lp-lbl">Paid</span><span className="lp-amt lp-paid">{fmt(totalPaid)}</span></div>
        <div className="lp-row lp-balance"><span className="lp-lbl">Balance Due</span><span className="lp-amt">{fmt(balance)}</span></div>
        {paidInFull && <div className="lp-paid-badge">✓ Paid in full</div>}
      </div>

      <div className="lp-list">
        {loading ? (
          <p className="lp-empty">Loading payments…</p>
        ) : payments.length === 0 ? (
          <p className="lp-empty">No payments recorded yet.</p>
        ) : (
          payments.map(p => (
            <div key={p.id} className="lp-item">
              <div className="lp-item-main">
                <span className="lp-item-amt">{fmt(p.amount)}</span>
                <span className="lp-item-method">{p.method || '—'}</span>
                <span className="lp-item-date">{fmtDate(p.payment_date || p.date_entered)}</span>
                <button className="lp-item-del" title="Delete payment" onClick={() => remove(p)}>×</button>
              </div>
              {p.notes && <div className="lp-item-notes">{p.notes}</div>}
            </div>
          ))
        )}
      </div>

      <div className="lp-add">
        <div className="lp-add-head">
          <h4>Record a payment</h4>
          <button type="button" className="lp-payfull" onClick={payFull} disabled={balance <= 0}>Pay in full</button>
        </div>
        <div className="lp-field-row">
          <label className="lp-field">Amount
            <input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount}
              onChange={e => set({ amount: e.target.value })} />
          </label>
          <label className="lp-field">Method
            <select value={form.method} onChange={e => set({ method: e.target.value })}>
              {methods.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
        </div>
        <label className="lp-field">Date
          <input type="date" value={form.payment_date} onChange={e => set({ payment_date: e.target.value })} />
        </label>
        <label className="lp-field">Notes
          <textarea rows={2} placeholder="Check #, reference, description…" value={form.notes}
            onChange={e => set({ notes: e.target.value })} />
        </label>
        {error && <div className="lp-error">{error}</div>}
        <button className="lp-add-btn" onClick={add} disabled={saving || !form.amount}>
          {saving ? 'Recording…' : 'Record payment'}
        </button>
      </div>
    </div>
  );
};

export default LedgerPayments;
